import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarRange, LoaderCircle, RotateCcw, X } from "lucide-react";
import type { Appointment } from "./types";
import { addIsoDays, istanbulToday } from "./lib/date";

const weekDayNames = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const scheduleHours = Array.from({ length: 15 }, (_, index) => `${String(index + 7).padStart(2, "0")}:00`);

type AppointmentRange = { from: string; to: string };

function workWeekStart(date: string) {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay() || 7;
  return addIsoDays(date, 1 - day);
}

function shortDate(date: string) {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    timeZone: "Europe/Istanbul"
  }).format(new Date(`${date}T12:00:00+03:00`));
}

function weekRangeLabel(from: string, to: string) {
  return `${shortDate(from)} – ${shortDate(to)}`;
}

export function WeeklyScheduleModal({
  open,
  initialDate,
  appointments,
  appointmentRange,
  loading,
  onLoadRange,
  onClose,
  onOpenAppointment
}: {
  open: boolean;
  initialDate: string;
  appointments: Appointment[];
  appointmentRange: AppointmentRange;
  loading: boolean;
  onLoadRange: (range: AppointmentRange) => Promise<void>;
  onClose: () => void;
  onOpenAppointment: (appointment: Appointment) => void;
}) {
  const [weekStart, setWeekStart] = useState(() => workWeekStart(initialDate));
  const [rangeLoadFailed, setRangeLoadFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const requestedRangeRef = useRef("");
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    setWeekStart(workWeekStart(initialDate));
    if (open) {
      requestedRangeRef.current = "";
      setRangeLoadFailed(false);
    }
  }, [open, initialDate]);

  const days = useMemo(
    () => weekDayNames.map((name, index) => ({ name, date: addIsoDays(weekStart, index) })),
    [weekStart]
  );
  const weekEnd = days.at(-1)?.date ?? addIsoDays(weekStart, 5);

  useEffect(() => {
    if (!open || loading) return;
    if (weekStart >= appointmentRange.from && weekEnd <= appointmentRange.to) {
      setRangeLoadFailed(false);
      return;
    }
    const nextRange = {
      from: weekStart < appointmentRange.from ? weekStart : appointmentRange.from,
      to: weekEnd > appointmentRange.to ? weekEnd : appointmentRange.to
    };
    const requestKey = `${nextRange.from}|${nextRange.to}`;
    if (requestedRangeRef.current === requestKey) return;
    requestedRangeRef.current = requestKey;
    setRangeLoadFailed(false);
    void onLoadRange(nextRange)
      .then(() => setRangeLoadFailed(false))
      .catch(() => setRangeLoadFailed(true));
  }, [open, loading, weekStart, weekEnd, appointmentRange.from, appointmentRange.to, onLoadRange, retryToken]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
    const handleDialogKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex='-1'])"
      ) ?? []).filter((element) => !element.hasAttribute("hidden"));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable.at(-1)!;
      const activeElement = document.activeElement;
      if (event.shiftKey && (activeElement === first || !dialogRef.current?.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeElement === last || !dialogRef.current?.contains(activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleDialogKeyboard);
    return () => {
      window.removeEventListener("keydown", handleDialogKeyboard);
      if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
    };
  }, [open]);

  if (!open) return null;

  const moveWeek = (difference: number) => setWeekStart((current) => addIsoDays(current, difference * 7));
  const goToCurrentWeek = () => setWeekStart(workWeekStart(istanbulToday()));
  const retryRangeLoad = () => {
    requestedRangeRef.current = "";
    setRangeLoadFailed(false);
    setRetryToken((current) => current + 1);
  };

  return (
    <>
      <button className="modal-scrim weekly-schedule-scrim" onClick={onClose} aria-label="Haftalık programı kapat" />
      <section ref={dialogRef} className="weekly-schedule-modal" role="dialog" aria-modal="true" aria-label="Haftalık program" tabIndex={-1}>
        <header className="weekly-schedule-head">
          <div className="weekly-schedule-title">
            <span className="weekly-schedule-icon"><CalendarRange size={22} /></span>
            <div>
              <span className="eyebrow">Tek bakışta stüdyo</span>
              <h2>Haftalık program</h2>
              <p>{weekRangeLabel(weekStart, weekEnd)} · Pazartesi–cumartesi</p>
            </div>
          </div>
          <div className="weekly-schedule-controls">
            <button className="button ghost compact" onClick={goToCurrentWeek}><RotateCcw size={15} /> Bu hafta</button>
            <div className="weekly-week-arrows">
              <button className="icon-button" onClick={() => moveWeek(-1)} aria-label="Önceki hafta"><ArrowLeft size={18} /></button>
              <button className="icon-button" onClick={() => moveWeek(1)} aria-label="Sonraki hafta"><ArrowRight size={18} /></button>
            </div>
            <button ref={closeButtonRef} className="icon-button weekly-close" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
          </div>
        </header>

        <div className="weekly-schedule-legend">
          <span><i className="planned" /> Planlandı</span>
          <span><i className="completed" /> Tamamlandı</span>
          <span><i className="blocked" /> Bloke</span>
          <small>İptal edilen seanslar boş saat olarak gösterilir.</small>
        </div>

        <div className="weekly-schedule-scroll">
          <div className="weekly-schedule-grid">
            <div className="weekly-corner"><span>Saat</span></div>
            {days.map((day) => (
              <div className={`weekly-day-head ${day.date === istanbulToday() ? "today" : ""}`} key={day.date}>
                <span>{day.name}</span>
                <strong>{shortDate(day.date)}</strong>
              </div>
            ))}

            {scheduleHours.flatMap((hour) => [
              <div className="weekly-hour" key={`hour-${hour}`}><strong>{hour}</strong></div>,
              ...days.map((day, dayIndex) => {
                const slotAppointments = appointments
                  .filter((appointment) => appointment.date === day.date && appointment.time === hour && appointment.status !== "cancelled")
                  .sort((left, right) => left.customer.localeCompare(right.customer, "tr"));
                return (
                  <div
                    className={`weekly-slot ${day.date === istanbulToday() ? "today" : ""}`}
                    data-week-slot={`${day.date}-${hour}`}
                    key={`${day.date}-${hour}`}
                  >
                    {slotAppointments.length > 0 && (
                      <div
                        className="weekly-slot-events"
                        style={{ gridTemplateColumns: `repeat(${slotAppointments.length}, minmax(0, 1fr))` }}
                      >
                        {slotAppointments.map((appointment) => (
                          <button
                            className={`weekly-event ${appointment.status} ${dayIndex >= 4 ? "tooltip-left" : ""}`}
                            data-appointment-id={appointment.id}
                            key={appointment.id}
                            onClick={() => onOpenAppointment(appointment)}
                            aria-label={`${appointment.customer}, ${appointment.type}, ${appointment.trainer}, ${hour}`}
                          >
                            <strong>{appointment.customer}</strong>
                            <span>{appointment.type}</span>
                            <small>{appointment.trainer}</small>
                            <span className="weekly-event-tooltip" aria-hidden="true">
                              <b>{appointment.customer}</b>
                              <em>{appointment.time}–{appointment.endTime} · {appointment.type}</em>
                              <em>{appointment.trainer} · {appointment.status === "planned" ? "Planlandı" : appointment.status === "completed" ? "Tamamlandı" : "Bloke"}</em>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ])}
          </div>
        </div>
        {loading && <div className="weekly-loading"><LoaderCircle size={20} /> Program yükleniyor…</div>}
        {!loading && rangeLoadFailed && (
          <div className="weekly-load-error" role="alert">
            <span>Haftalık program yüklenemedi.</span>
            <button className="button ghost compact" onClick={retryRangeLoad}>Tekrar dene</button>
          </div>
        )}
      </section>
    </>
  );
}
